import {useCallback, useState, useEffect}   from 'react';

import FindCase                             from './FindCase'
import {PatientConfirm}            from './Patient'
import useBookingState, {BS_STATES, B_ACTIONS}         
    from './bookingReducer'
import BookingSummary, {BookingHeading}     from './BookingSummary'
import BookingData                          from './BookingData'
import BookingEditor                        from './BookingEditor';
import BookingDescriptionForm               from './BookingDescriptionForm'

import {OKCancel, clearLocationParams, getURLSearchParam}  
                                            from '../../lib/iprs-react-library/src/index.js';
import AppTypeAndPersonSelect from './comp/AppTypeAndPersonSelect';
import ReschedulePickTime from './comp/ReschedulePickTime';
/* 
 * Component to display a booking screen
 */
const Booking = ({api, state, eventDispatch}) => {
    
    const [errorMessage, setError]                  = useState('')                               
    
    /* extract the list of appoinmtnt types, which has been captured elsewhere 
     * in the network */
    const { appType: { appointmentTypes, Groups: appTypeGroups } }         = state
    
    /* filter the booking state from the wider application state */
    //const bookingState                              = getBookingState( state )                  
    
    const [bookingState, bookingDispatch] = useBookingState(state, eventDispatch)
    if( !bookingState.newBooking ){
        bookingState.newBooking                = new BookingData();
    }

    const isLoggedIn = api.isLoggedIn();

    useEffect( () => {
        
        const newCaseID = getURLSearchParam( 'caseID' );
        if(newCaseID && isLoggedIn){
            clearLocationParams();
            api.transact( 'PWPatientOverview', { CaseID: newCaseID } )
            .then( r => r.apiResult )
            .then( r => bookingDispatch( B_ACTIONS.PATIENT_DATA, {pkCaseID: newCaseID, ...r} ) )
            .catch( err => bookingDispatch( B_ACTIONS.NEW_URL_CASE_ERROR, err.apiText ) )
        }
    }, [api, isLoggedIn, bookingDispatch] )
    
    /* Extract fields from the internal state of this component */
    const { pageState, newBooking, holdBooking = null } = bookingState
    
    const bookingID     = newBooking.bookingID;
    
    const makeBooking = useCallback( e => {
        
        e.preventDefault()      
        
        const bookingID = newBooking.bookingID; // held when clicked on slot
        const caseID    = newBooking.patient.internalCaseID
        
        if( bookingID ){
            api.transact("FinaliseSlot", {
                BookingID: bookingID,
                CaseID: caseID,
                Description: newBooking.description,
                IsVisual: newBooking.isVisual,
                EarliestDateTimeOffered: newBooking.earliestDateTimeOffered,
                AppointmentTimeReason: newBooking.apptTimeExplanation,
            })
            .then( r => r.apiResult ) 
            .then(r => bookingDispatch(B_ACTIONS.BOOKING_FINALIZED, r) )
            .catch(err => setError(err.message));
        }else{
            // Should never happen
            throw new Error("Cannot book without holding the time first");
        }
    
    }, [newBooking, api, bookingDispatch] )
    
    const resceduleBooking = useCallback(e => {
        
        api.rescheduleBooking(bookingID, newBooking.getRescheduleID())
            .then(r => r.apiResult)
            .then(r => bookingDispatch(B_ACTIONS.FINISHED, r))
            .catch(err => setError(err.message));
    }, [api, bookingDispatch, bookingID, newBooking] )
    
    const updateBookingDescription = useCallback( text => {
        
        api.updateBookingDescription(   newBooking.bookingID,
                                        text )
        .then( r => bookingDispatch( B_ACTIONS.FINISHED, text ) )
        .catch( err => setError( err.message ) )
    }, [newBooking.bookingID, api, bookingDispatch ])
    
    const deleteBooking = () => {
        
        api.deleteBooking( bookingID )
            .then( r => r.apiResult )
            .then( a => bookingDispatch(B_ACTIONS.CANCEL_CONFIRM) )
            .catch( err => setError( err.message ) )
    }
    
    const finalise = e => {
        bookingDispatch(B_ACTIONS.CANCEL_CONFIRM)
    }
    
    const handleAppoinmentTypeSelected = sat => {
        
        if( null != newBooking.start && newBooking.clinician.partyID ){
            api.holdPartySlot(  sat.AppointmentTypeID, 
                                sat.PartyID,
                                newBooking.start, 
                                newBooking.end)
            .then( r => r.apiResult )
            .then( r => { 
                setError('');
                r.isVisual = sat.isVisual;
                bookingDispatch( B_ACTIONS.PERSONAL_TIME, r );
            } )
            .catch( err => setError(err.message) );
        }else{
            bookingDispatch(B_ACTIONS.APPOINTMENT_TYPE, sat );
        }
    }
    
    const goAppTypeCalendar = e => {

        e.preventDefault();
        
        eventDispatch( { appType: { appointmentTypeID: 
                                    newBooking.getAppoinmnetTypeID() } } )
    }
    
    const content = () => {
        
        let content = '';
        
        switch ( pageState ){
            case 'find_case':
                
                content = (<>
                
                    <h1 className={'calHead'} >Make Booking</h1>
                    <FindCase bookingDispatch={bookingDispatch} api={api} setError={setError} />
                </>)
                break;
                
            case BS_STATES.BS_PATIENT_FOUND:
            
                content = <>
                
                        <h1 className={'calHead'} >Make Booking</h1>
                        <h2>Confirm the Patient Details</h2>
                        <PatientConfirm patientRecord={newBooking} 
                            bookingDispatch={bookingDispatch} 
                            text={'Confirm'} />
                    </>
                break;
                
            case 'select_app_type':
                
                /* if we have a list of available appointmnet type IDs then
                filter the list to only include the available ones. */
                let appTypes;
                if ( Array.isArray( newBooking.availableAppTypes ) ){
                    appTypes = appointmentTypes.filter( at => newBooking.availableAppTypes.includes( at.AppointmentTypeID ) )
                }else{
                    appTypes = appointmentTypes;
                }
                const clinician = Number.parseInt( newBooking?.clinician?.partyID?? 0, 10);
                
                content = 
                        <>
                        <h1 className={'calHead'} >Make Booking</h1>
                        <BookingHeading booking={newBooking} />

                    <AppTypeAndPersonSelect appointmentTypes={appTypes}
                        clinician={clinician}
                        appTypeGroups={appTypeGroups}
                        onSelect={handleAppoinmentTypeSelected}
                        onCancel={bookingDispatch}
                            api={ api }/>


                    </>
                break;

            case 'select_time':
            
                content = <>
                
                    <h1 className={'calHead'} >Make Booking</h1>
                    <BookingHeading booking={newBooking} />
                    <p>Select an available time for this booking by going to the appointment type calendar</p>
                    <button onClick={goAppTypeCalendar} >Go To Appointment Type Calendar</button>
                    <p>Or go to one of the team calendars and select time in a person's individual calendar.</p>
                    
                    <a href='.' onClick={ e => {e.preventDefault(); eventDispatch({booking: {
                                pageState: 'select_app_type'} } )} } >go back</a>
                </>
                break
            case BS_STATES.BS_ADD_DESCRIPTION:
            
                content = <>
                    <h1 className={'calHead'} >Add a Description</h1>
                    <label><em>This is not part of the patient record.</em></label>
                    
                    <BookingDescriptionForm  
                        description={newBooking.description}
                        onOK={text => updateBookingDescription(text)} 
                        onCancel={finalise}  />
                    
                    
                </>
                
                break;
                
            case BS_STATES.BS_CAPTURE_DESCRIPTION:
            
                content = <>
                    <h1 className={'calHead'} >Add a Description</h1>
                    <label><em>This is not part of the patient record.</em></label>
                    
                    <BookingDescriptionForm  
                        description={newBooking.description}
                        onOK={text => bookingDispatch( B_ACTIONS.DESCRIPTION, text )} 
                        onCancel={finalise}  />
                    
                    
                </>
                
                break;

            case BS_STATES.BS_CAPTURE_APPT_TIME_REASON:
            
                content = <>
                    <h1 className={'calHead'} >Reason appointment is outside 5 working days</h1>
                    
                    
                    <BookingDescriptionForm  
                        description={newBooking.apptTimeExplanation}
                        onOK={text => bookingDispatch( B_ACTIONS.APPT_TIME_REASON, text )} 
                        onCancel={finalise}  />
                    
                    
                </>
                
                break;
            case BS_STATES.BS_CONFIRM:
            
                content = <>
                    <h1 className={'calHead'} >Confirm Booking</h1>
                    <BookingSummary booking={newBooking} />
                    <OKCancel name='confirm' onOK={makeBooking} 
                        onCancel={finalise} okText='Confirm Booking' />
                </>
                
                break;
            case BS_STATES.BS_DESCRIPTION_CHANGE_SUMMARY:
            case BS_STATES.BS_RESCHEDULE_SUMMARY:
            case BS_STATES.BS_SUMMARY:
            
                content = <>
                    <h1 className={'calHead'} >Booking {BS_STATES.BS_SUMMARY === pageState? 'Created': 'Updated'}</h1>
                    <BookingSummary booking={newBooking} />
                    <button className={'singleBtn'} name='finalise' onClick={finalise} >Finish</button>
                </>
            break
            
                
            case BS_STATES.BS_RESCHEDULE_SELECT_TIME:
            
                content = <>
                    
                     <h1 className={'calHead'} >Reschedule Booking</h1>
                     <BookingHeading booking={newBooking} />
                    
                    <ReschedulePickTime api={api} clinician={newBooking.clinician.partyID} 
                        goAppTypeCalendar={goAppTypeCalendar} newBooking={newBooking} 
                        bookingDispatch={bookingDispatch}
                        selectedAppointmentTypeID={newBooking.appointmentType.appointmentTypeID}  />

                    <div style={{clear: 'left'}} >
                        <a href='.' onClick={ e => {e.preventDefault(); eventDispatch({booking: {
                            pageState: 'select_app_type'} } )} } >go back</a>
                    </div>
                </>
                break;
            case BS_STATES.BS_RESCHEDULE_CONFIRM:
            
                content = <>
                
                    <h1 className={'calHead'} >Confirm Reschedule</h1>
                    <BookingSummary booking={newBooking} />
                    <h1>Confirm New Clinician and Time</h1>
                    <div>Start: {holdBooking.getStartString()}</div><div>End: {holdBooking.getEndString()}</div>
                    <div>Clinician: {holdBooking.clinician.name}</div><br/>
                    {(newBooking.getAppoinmnetTypeID()!==holdBooking.getAppoinmnetTypeID())&&<div>Appointment Type: {holdBooking.appointmentType.title}</div>}
                    <OKCancel name='confirm' onOK={resceduleBooking} 
                        onCancel={finalise} okText='Confirm Reschedule' />
                </>
                break;
                
            case BS_STATES.BS_DELETE_CONFIRM:
            
                content = <>
                    <h1 className={'calHead'} >Confirm</h1>
                    <BookingSummary booking={newBooking} />
                    <h1>Confirm Delete</h1>
                    <OKCancel name='confirm' onOK={deleteBooking} 
                        onCancel={finalise} okText='Delete Booking' />
                </>
                break;
            default:
                content = <p>No content. Booking state = '{pageState}'</p>
        }
        
        return content
    }
    
    // const res = rescheduleInProgress( bookingState )
    
    const {lastAction=''} = bookingState;
    
    return <div>

        {newBooking && false && < BookingEditor booking={newBooking} pageState={pageState} lastEvent={lastAction} />}
       
        {content()}
        
        <div style={{fontWeight: 'bold'}}>{bookingState.warning&&bookingState.warning}</div>
        <br/><br/><br/>
        
        <div>{errorMessage}</div>
        
        
    </div>
}








export default Booking
